import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  Users,
  Armchair,
  CalendarDays,
  ChevronRight,
  Clock,
} from "lucide-react";
import { format, addDays } from "date-fns";
import { es } from "date-fns/locale";
import api from "../lib/api-client";

/**
 * Panel de una sede con el módulo de Clases: sustituye al panel de mesas
 * (que no aplica a una escuela). Reutiliza los endpoints del módulo:
 * /classes/agenda (hoy) y /classes/calendar (alumnos por día de la semana).
 * El conmutador vive en App.tsx (PanelRoute): así ni este panel ni el de
 * mesas ejecutan hooks del otro mundo.
 */

interface PanelSession {
  template: {
    id: string;
    name: string;
    emoji: string | null;
    teacherName: string | null;
    durationMinutes: number;
    startTime: string;
  };
  capacity: number;
  booked: number;
  remaining: number;
  cancelled: boolean;
}

interface DayInfo {
  date: string;
  total: number;
  cancelled: number;
  booked: number;
}

export default function ClassesPanel() {
  const todayStr = format(new Date(), "yyyy-MM-dd");

  const [sessions, setSessions] = useState<PanelSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [weekDays, setWeekDays] = useState<Record<string, DayInfo>>({});

  const fetchToday = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/classes/agenda", {
        params: { date: todayStr },
      });
      setSessions(response.data.data || []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [todayStr]);

  // Alumnos por día de los próximos 7 días (puede cruzar de mes).
  const fetchWeek = useCallback(async () => {
    const months = Array.from(
      new Set([
        format(new Date(), "yyyy-MM"),
        format(addDays(new Date(), 6), "yyyy-MM"),
      ]),
    );
    try {
      const lists = await Promise.all(
        months.map((m) =>
          api
            .get("/classes/calendar", { params: { month: m } })
            .then((r) => r.data.data?.days || []),
        ),
      );
      const map: Record<string, DayInfo> = {};
      for (const list of lists) for (const d of list) map[d.date] = d;
      setWeekDays(map);
    } catch {
      // sin datos, las tarjetas de semana salen a 0
    }
  }, []);

  useEffect(() => {
    fetchToday();
    fetchWeek();
  }, [fetchToday, fetchWeek]);

  const open = sessions.filter((s) => !s.cancelled);
  const studentsToday = open.reduce((sum, s) => sum + s.booked, 0);
  const freeToday = open.reduce((sum, s) => sum + s.remaining, 0);

  const next7 = Array.from({ length: 7 }, (_, i) =>
    format(addDays(new Date(), i), "yyyy-MM-dd"),
  );
  const studentsWeek = next7.reduce(
    (sum, d) => sum + (weekDays[d]?.booked ?? 0),
    0,
  );

  const nowTime = format(new Date(), "HH:mm");
  const nextClass = open.find((s) => s.template.startTime > nowTime);

  const pill = (s: PanelSession) => {
    if (s.cancelled) return "bg-gray-200 text-gray-500";
    if (s.remaining <= 0) return "bg-red-100 text-red-700";
    if (s.remaining <= s.capacity * 0.25) return "bg-amber-100 text-amber-700";
    return "bg-green-100 text-green-700";
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Cabecera */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-primary-600" />
            Panel
          </h1>
          <p className="text-sm text-gray-500 capitalize">
            {format(new Date(), "EEEE, d 'de' MMMM", { locale: es })}
          </p>
        </div>
        <Link
          to="/classes"
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg self-start sm:self-auto"
        >
          Ir a la agenda
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Tarjetas resumen */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <GraduationCap className="w-3.5 h-3.5" /> Clases hoy
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">
            {loading ? "—" : open.length}
          </p>
          {!loading && sessions.length > open.length && (
            <p className="text-xs text-red-600">
              {sessions.length - open.length} anulada
              {sessions.length - open.length > 1 ? "s" : ""}
            </p>
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Alumnos hoy
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">
            {loading ? "—" : studentsToday}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <Armchair className="w-3.5 h-3.5" /> Plazas libres hoy
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">
            {loading ? "—" : freeToday}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5" /> Alumnos 7 días
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">
            {studentsWeek}
          </p>
        </div>
      </div>

      {/* Clases de hoy */}
      <div className="bg-white rounded-xl border border-gray-200 mb-5 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Clases de hoy</h2>
          {nextClass && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Próxima: {nextClass.template.startTime} ·{" "}
              {nextClass.template.name}
            </span>
          )}
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-28">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary-600"></div>
          </div>
        ) : sessions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <GraduationCap className="w-9 h-9 mx-auto mb-2 text-gray-300" />
            Hoy no hay clases programadas
          </div>
        ) : (
          sessions.map((s) => (
            <Link
              key={s.template.id}
              to={`/classes?date=${todayStr}`}
              className={`flex items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 ${
                s.cancelled ? "opacity-60" : ""
              }`}
            >
              <span className="min-w-0 text-sm">
                <span className="font-bold tabular-nums">
                  {s.template.startTime}
                </span>{" "}
                <span className="font-medium text-gray-900">
                  {s.template.emoji ? `${s.template.emoji} ` : ""}
                  {s.template.name}
                </span>
                {s.template.teacherName && (
                  <span className="text-gray-500">
                    {" "}
                    · {s.template.teacherName}
                  </span>
                )}
              </span>
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${pill(s)}`}
              >
                {s.cancelled ? "Anulada" : `${s.booked} / ${s.capacity}`}
              </span>
            </Link>
          ))
        )}
      </div>

      {/* Próximos 7 días */}
      <div className="bg-white rounded-xl border border-gray-200 p-3">
        <h2 className="font-semibold text-gray-900 text-sm mb-2 px-1">
          Próximos 7 días
        </h2>
        <div className="grid grid-cols-7 gap-1.5">
          {next7.map((d) => {
            const info = weekDays[d];
            const closed = info && info.total > 0 && info.cancelled === info.total;
            return (
              <Link
                key={d}
                to={`/classes?date=${d}`}
                className={`rounded-lg py-1.5 text-center border transition-colors ${
                  closed
                    ? "bg-red-50 border-red-200 hover:bg-red-100"
                    : info
                      ? "bg-primary-50 border-primary-200 hover:bg-primary-100"
                      : "bg-white border-gray-200 text-gray-400"
                }`}
              >
                <div className="text-[10px] uppercase font-medium text-gray-600">
                  {format(new Date(`${d}T00:00:00`), "EEE", { locale: es })}
                </div>
                <div className="text-sm font-bold tabular-nums text-gray-900">
                  {format(new Date(`${d}T00:00:00`), "d")}
                </div>
                <div
                  className={`text-[10px] font-medium ${
                    closed
                      ? "text-red-600"
                      : info
                        ? "text-primary-600"
                        : "text-transparent"
                  }`}
                >
                  {closed ? (
                    "Cerrado"
                  ) : info ? (
                    <span className="inline-flex items-center justify-center gap-0.5">
                      <Users className="w-2.5 h-2.5" />
                      {info.booked}
                    </span>
                  ) : (
                    "·"
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
